import { useCallback } from 'react'
import { useTelemetry } from './useTelemetry'

/**
 * Hook to track custom events
 *
 * Returns a stable callback function for event tracking.
 * This is the primary way to replace react-ga4 event calls.
 *
 * @returns Function to track events
 *
 * @example
 * ```tsx
 * // Basic usage
 * function AddToCartButton({ productCode }) {
 *   const trackEvent = useTrackEvent()
 *
 *   const handleClick = () => {
 *     trackEvent('add_to_cart', { product_code: productCode })
 *     // ... add to cart logic
 *   }
 *
 *   return <button onClick={handleClick}>Add to Cart</button>
 * }
 *
 * // Migration from react-ga4
 * // Before: reactGa.event({ category: 'Cart', action: 'Add Item' })
 * // After:  trackEvent('cart.add_item', { category: 'Cart' })
 * ```
 */
export function useTrackEvent() {
  const telemetry = useTelemetry()

  return useCallback(
    (eventName: string, attributes?: Record<string, string>) => {
      telemetry?.trackEvent(eventName, attributes)
    },
    [telemetry]
  )
}

/**
 * Hook to track click events with automatic event naming
 *
 * @param eventName - Base event name
 * @param baseAttributes - Attributes to include with every click
 * @returns Click handler function
 *
 * @example
 * ```tsx
 * function NavButton({ label }) {
 *   const handleClick = useTrackClick('nav_button_click', { label })
 *   return <button onClick={handleClick}>{label}</button>
 * }
 * ```
 */
export function useTrackClick(eventName: string, baseAttributes?: Record<string, string>) {
  const trackEvent = useTrackEvent()

  return useCallback(
    (additionalAttributes?: Record<string, string>) => {
      trackEvent(eventName, { ...baseAttributes, ...additionalAttributes })
    },
    [trackEvent, eventName, baseAttributes]
  )
}

/**
 * Hook to track form submissions
 *
 * @param formName - Name of the form
 * @returns Object with track functions for submission states
 *
 * @example
 * ```tsx
 * function ContactForm() {
 *   const formTracker = useTrackForm('contact_form')
 *
 *   const handleSubmit = async (data) => {
 *     try {
 *       await submitForm(data)
 *       formTracker.trackSuccess()
 *     } catch (error) {
 *       formTracker.trackError(error.message)
 *     }
 *   }
 *
 *   return <form onSubmit={handleSubmit}>...</form>
 * }
 * ```
 */
export function useTrackForm(formName: string) {
  const trackEvent = useTrackEvent()

  return {
    trackSubmit: useCallback(
      (attributes?: Record<string, string>) => {
        trackEvent(`form.${formName}.submit`, attributes)
      },
      [trackEvent, formName]
    ),

    trackSuccess: useCallback(
      (attributes?: Record<string, string>) => {
        trackEvent(`form.${formName}.success`, attributes)
      },
      [trackEvent, formName]
    ),

    trackError: useCallback(
      (errorMessage?: string, attributes?: Record<string, string>) => {
        trackEvent(`form.${formName}.error`, {
          error_message: errorMessage,
          ...attributes,
        })
      },
      [trackEvent, formName]
    ),

    trackFieldInteraction: useCallback(
      (fieldName: string, attributes?: Record<string, string>) => {
        trackEvent(`form.${formName}.field_interaction`, {
          field_name: fieldName,
          ...attributes,
        })
      },
      [trackEvent, formName]
    ),
  }
}
